/**
 * This file is modified from the original source code of src/app/components/task-card/task-title.tsx
 * The main difference is to remove business logic (generally using task entity) and make it a reusable dumb component
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {TaskColor} from 'yougile-model'
import {TextLoader} from '@components/common/base/text-loader'
import {TextWithUrl} from '@components/common/base/text-with-url'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useIdTaskUpdater} from '@components/id-tasks/use-id-task-updater'
import {TextArea} from '@components/textarea'
import {keyboardHandler, noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'

const TwTitle = tw.div<{$completed?: boolean}>`
  relative
  truncate
  isolate
  flex-1
  min-w-0
  break-words
  text-sm-regular
  ml-4
  ${p =>
    p.$completed
      ? cn('text-placeholder-new', 'group-hover:text-primary')
      : 'text-primary'}
`

// Zero width to always render after the last symbol on the same text line
const TwEditButtonContainer = tw.div`
  relative
  isolate
  inline-block
  h-12
`

const TwIconArchived = tw(IconTabler)<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
}>`
  -mb-[3px]
  mr-4

  ${p => {
    if (p.$isTaskColored) {
      return `text-icon-primary ${
        p.$isCompleted ? 'opacity-[.38]' : 'opacity-[.64]'
      }`
    }
    return 'text-icon-secondary'
  }}
`

const MAX_TITLE_LENGTH_TO_SHOW_IN_TOOLTIP = 1024

const TwTextWithUrl = tw(TextWithUrl)<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
}>`
  ${p => {
    if (p.$isTaskColored) {
      return `text-primary ${p.$isCompleted ? 'opacity-[.38]' : 'opacity-100'}`
    }
    return ''
  }}
`

interface TaskTitleProps {
  value: string
  onValueChange?: (value: string) => void
  isArchived?: boolean
  isLazy?: boolean
  color?: TaskColor | null
  className?: string
  editable?: boolean
  isEditing?: boolean
  setEdit?: (edit: boolean) => void
  onSubmitValue?: (value: string) => void
  completed: boolean
  fromBackgroundColor?: string
  tooltipContent?: string
  placeholder?: string
}

/**
 * Get appropriate editor for the two different modes of editing (controlled and uncontrolled)
 * @param props
 */
const getEditor = (
  props: Pick<TaskTitleProps, 'value' | 'onValueChange' | 'onSubmitValue'> & {
    isColored: boolean
    setEdit: (flag: boolean) => void
    placeholder?: string
  }
): React.ReactNode => {
  const {value, onValueChange, setEdit, onSubmitValue, isColored, placeholder} =
    props
  return onValueChange ? (
    <ControlledEditor
      value={value}
      onValueChange={onValueChange}
      setEdit={setEdit}
      onSubmit={onSubmitValue}
      isTaskColored={isColored}
      placeholder={placeholder}
    />
  ) : (
    <Editor
      externalValue={value}
      setEdit={setEdit}
      onSubmit={onSubmitValue}
      isTaskColored={isColored}
    />
  )
}

export const ItemName = forwardRef<HTMLElement, TaskTitleProps>(
  (props, ref) => {
    const titleRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(
      null
    ) as React.MutableRefObject<HTMLDivElement | null>
    const buttonRef = useRef<HTMLDivElement | null>(
      null
    ) as React.MutableRefObject<HTMLDivElement | null>
    const urlAtEndRef = useRef<boolean>(false)
    const [titleWidth, setTitleWidth] = useState(0)

    const {
      value,
      onValueChange,
      completed, // Rename to isCompleted
      isArchived,
      isLazy,
      className,
      color,
      editable,
      isEditing,
      setEdit,
      onSubmitValue,
      fromBackgroundColor,
      tooltipContent,
      placeholder
    } = props
    const localTextRef = useRef<HTMLElement | null>(
      null
    ) as React.MutableRefObject<HTMLElement | null>
    const [tooltipIsDisabled, setTooltipIsDisabled] = useState(false)
    const tooltipIsDisabledRef = useRef(false)
    const setTextProxyRef = (el: HTMLElement | null) => {
      localTextRef.current = el
      if (ref) {
        if (typeof ref === 'function') {
          ref(el)
        } else if (ref) {
          ref.current = el
        }
      }
    }

    const isColored = color !== null

    const refreshPosition = useCallback(() => {
      if (titleRef.current && containerRef.current && buttonRef.current) {
        if (urlAtEndRef.current) {
          // Display a button as a 16x16 to not render over the last link button
          buttonRef.current.style.left = '0px'
          buttonRef.current.style.width = `${iconWidth}px`
          buttonRef.current.style.paddingLeft = '0px'
          containerRef.current.style.width = `${iconWidth}px`
        } else {
          // Display a button as a 22x16 block that can cover last chars of text
          const {right} = titleRef.current.getBoundingClientRect()
          const {left} = containerRef.current.getBoundingClientRect()
          const delta = Math.min(right - left, bgWidth) - bgWidth
          buttonRef.current.style.left = `${delta}px`
          buttonRef.current.style.width = `${bgWidth}px`
          buttonRef.current.style.paddingLeft = `${paddingLeft}px`
          containerRef.current.style.width = `0px`
        }
      }
    }, [])

    const onTitleResize = useCallback((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.target === titleRef.current) {
          setTitleWidth(entry.contentRect.width)
        }
      }
    }, [])

    useEffect(() => {
      const resizeObserver = new ResizeObserver(onTitleResize)
      if (titleRef.current) {
        resizeObserver.observe(titleRef.current)
      }
      return () => {
        if (titleRef.current) {
          resizeObserver.unobserve(titleRef.current)
        }
      }
    }, [onTitleResize])

    useEffect(() => {
      refreshPosition()
    }, [value, isEditing, titleWidth, editable])

    useIdTaskUpdater()

    const limitedTooltipContent = Util.limitStr(
      tooltipContent || '',
      MAX_TITLE_LENGTH_TO_SHOW_IN_TOOLTIP
    )

    const refreshTooltipRequirement = useCallback(() => {
      const el = localTextRef.current
      if (el) {
        const overflowed =
          el.offsetWidth > (el.parentElement?.parentElement?.offsetWidth ?? 0)
        if (overflowed === tooltipIsDisabledRef.current) {
          setTooltipIsDisabled(!overflowed)
          tooltipIsDisabledRef.current = !overflowed
        }
      }
    }, [])

    useEffect(() => {
      refreshTooltipRequirement()
    }, [value])

    return (
      <TwTitle
        ref={titleRef}
        $completed={completed}
        data-test="board-task-title"
        className={className}
        onMouseEnter={refreshTooltipRequirement}
      >
        {isEditing && setEdit ? (
          getEditor({
            value,
            onValueChange,
            setEdit,
            onSubmitValue,
            isColored,
            placeholder
          })
        ) : (
          <>
            {isArchived && (
              <Tooltip
                content={Lang.msg.taskCompleted}
                placement="bottom-start"
              >
                <TwIconArchived
                  icon="IconArchive"
                  viewBox="0 0 24 24"
                  customSize={16}
                  $isCompleted={completed}
                  $isTaskColored={isColored}
                />
              </Tooltip>
            )}
            <Tooltip
              offset={[0, 10]}
              content={limitedTooltipContent}
              disabled={tooltipIsDisabled}
            >
              <span>
                {isLazy && <TextLoader />}
                {!isLazy && (
                  <TwTextWithUrl
                    ref={setTextProxyRef}
                    $isCompleted={completed}
                    $isTaskColored={isColored}
                    text={value}
                    bottom={-3}
                    onUpdate={x => (urlAtEndRef.current = x.urlAtEnd)}
                  />
                )}
              </span>
            </Tooltip>
            {!globals.sharedId && editable && (
              <TwEditButtonContainer ref={containerRef}>
                <EditButton
                  isCompleted={completed}
                  isTaskColored={isColored}
                  taskColor={color || undefined}
                  fromBackgroundColor={fromBackgroundColor}
                  ref={buttonRef}
                  onClick={e => {
                    noPropagate(e)
                    setEdit?.(true)
                  }}
                />
              </TwEditButtonContainer>
            )}
          </>
        )}
      </TwTitle>
    )
  }
)

const TwTextArea = tw(TextArea)<{$isTaskColored?: boolean}>`
  w-full
  border-solid
  border-0
  border-b
  focus:border-0
  focus:border-b
  text-sm-regular
  text-primary

  ${p =>
    p.$isTaskColored
      ? 'border-icon-primary focus:border-icon-primary'
      : 'border-main-old focus:border-main-old'}

  bg-transparent
  focus:bg-transparent

  overflow-hidden
  -mb-1
  cursor-text
`

const ControlledEditor = (props: {
  value: string
  onValueChange: (value: string) => void
  onBlur?: () => void
  setEdit: (flag: boolean) => void
  onSubmit?: (value: string) => void
  isTaskColored?: boolean
  placeholder?: string
}) => {
  const {value, onValueChange, placeholder} = props
  const submit = () => {
    props.onSubmit?.(value)
    return true
  }
  const cancel = () => {
    onValueChange('')
    props.setEdit(false)
    return true
  }

  return (
    <TwTextArea
      autoFocus
      $isTaskColored={props.isTaskColored}
      onKeyDown={keyboardHandler({enter: submit, escape: cancel})}
      onFocus={e =>
        e.target.setSelectionRange(e.target.value.length, e.target.value.length)
      }
      onMouseDown={noPropagate}
      onClick={noPropagate}
      onBlur={submit}
      value={value}
      onChange={e => onValueChange(e.target.value)}
      placeholder={placeholder}
    />
  )
}

const Editor = (props: {
  externalValue: string
  onValueChange?: (value: string) => void
  onBlur?: () => void
  setEdit: (flag: boolean) => void
  onSubmit?: (value: string) => void
  isTaskColored?: boolean
  placeholder?: string
}) => {
  const [value, setValue] = useState(props.externalValue)
  const originalValueRef = useRef(value)
  const submit = () => {
    // update title only if it's changed to prevent concurrent updates problem
    if (originalValueRef.current !== value) {
      props.onSubmit?.(value)
    }
    props.setEdit(false)
    return true
  }
  const cancel = () => {
    props.setEdit(false)
    return true
  }

  return (
    <TwTextArea
      autoFocus
      $isTaskColored={props.isTaskColored}
      onKeyDown={keyboardHandler({enter: submit, escape: cancel})}
      onFocus={e =>
        e.target.setSelectionRange(e.target.value.length, e.target.value.length)
      }
      onMouseDown={noPropagate}
      onClick={noPropagate}
      onBlur={submit}
      value={value}
      onChange={e => setValue(e.target.value)}
      placeholder={props.placeholder}
    />
  )
}

// Width&paddings are defined using styles upon `refresh`
const TwEditButton = tw.div<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
  $fromBackground?: string
}>`
  absolute
  -bottom-3
  h-16
  bg-transparent
  bg-gradient-to-l
  from-65%
  to-100%
  cursor-pointer
  invisible
  group-hover:visible

  ${p => {
    return `${p.$fromBackground} to-[#ffffff00]`
  }}

  ${p => {
    return p.$isTaskColored
      ? `text-icon-primary`
      : `${
          p.$isCompleted ? 'text-placeholder-new' : 'text-icon-secondary'
        } hover:text-action-hover active:text-action-pressed`
  }}
`

const TwIconTabler = tw(IconTabler)<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
}>`
  ${p => {
    if (p.$isTaskColored) {
      return p.$isCompleted
        ? 'opacity-[.38] hover:opacity-[.64]'
        : 'opacity-[.64] hover:opacity-100'
    }
    return ''
  }}
`

const iconWidth = 16
const paddingLeft = 6
// Should be the same as TwEditButtonBackground width
const bgWidth = iconWidth + paddingLeft

interface EditButtonProps {
  onClick: (e: React.MouseEvent) => void
  isTaskColored?: boolean
  isCompleted?: boolean
  className?: string
  taskColor?: TaskColor
  fromBackgroundColor?: string
}

// Tailwind doesn't support template strings like `from-${color}` if they are not described in tailwind-colors config
// 'from-[color]' are not such variables that should be described in config, so we placed it here where it's used
const backgroundColors: Record<TaskColor, string> = {
  'task-blue': 'from-task-blue',
  'task-green': 'from-task-green',
  'task-pink': 'from-task-pink',
  'task-primary': 'from-task-primary',
  'task-red': 'from-task-red',
  'task-turquoise': 'from-task-turquoise',
  'task-violet': 'from-task-violet',
  'task-yellow': 'from-task-yellow',
  'task-gray': 'from-task-gray'
}

const EditButton = forwardRef<HTMLDivElement, EditButtonProps>(
  (
    {
      onClick,
      className,
      isTaskColored,
      fromBackgroundColor,
      taskColor,
      isCompleted
    },
    ref
  ) => {
    const fromBackground =
      fromBackgroundColor ??
      (isTaskColored && taskColor
        ? backgroundColors[taskColor]
        : 'from-background-card')

    return (
      <Tooltip
        content={Lang.msg.taskRename}
        placement="bottom-start"
        offset={[5, 5]}
      >
        <TwEditButton
          data-test="board-task-pancel"
          ref={ref}
          onClick={onClick}
          $isTaskColored={isTaskColored}
          $isCompleted={isCompleted}
          $fromBackground={fromBackground}
          className={className}
        >
          <TwIconTabler
            $isTaskColored={isTaskColored}
            $isCompleted={isCompleted}
            icon="IconPencil"
            viewBox="0 0 24 24"
            customSize={16}
          />
        </TwEditButton>
      </Tooltip>
    )
  }
)
